import { isLoopEvent, type AudioEvent, type LoopEvent } from '@tunasong/schemas'
import { useEffect, useRef } from 'react'

/** Manage loop playback */
export const useLoopControl = ({
  position,
  activeLoop,
  events,
  onLoopStart,
  onLoopEnd,
}: {
  position: number
  activeLoop?: LoopEvent
  events: AudioEvent[]
  onLoopStart?: (event: LoopEvent) => void
  onLoopEnd?: (event: LoopEvent) => void
}) => {
  // const [position, setPosition] = useState<number>(0)
  const prevPos = useRef<number>(null)

  // Annotated events with enabled state for loops
  const eventsAnnotated =
    events?.map(event =>
      isLoopEvent(event)
        ? {
            ...event,
            enabled: activeLoop?.id === event.id,
          }
        : event
    ) ?? []

  // Find loops that are either starting or ending between the current position and the previous position
  const prevPosSeconds = prevPos.current ?? 0

  // Set the active loop if we have started a loop
  useEffect(() => {
    const loops = events.filter(
      event =>
        event.type === 'loop' &&
        ((event.start >= prevPosSeconds && event.start <= position) ||
          (event.end >= prevPosSeconds && event.end <= position))
    ) as LoopEvent[]

    const startLoops = loops.filter(loop => loop.start >= prevPosSeconds && loop.start <= position)
    if (startLoops.length > 0 && onLoopStart) {
      startLoops.forEach(loop => onLoopStart(loop))
    }
    const endLoops = loops.filter(loop => loop.end && loop.end >= prevPosSeconds && loop.end <= position)
    if (endLoops.length > 0 && onLoopEnd) {
      endLoops.forEach(loop => onLoopEnd(loop))
    }
    prevPos.current = position
  }, [events, onLoopEnd, onLoopStart, position, prevPosSeconds])

  return { events: eventsAnnotated }
}
